import { alpha, Theme } from '@mui/material/styles'

// Shared column grid + row metrics for the Live Event Analytics adopters tree
// table: game rows and their event rows render into the same `<colgroup>`.

export type DrilldownColumnKey = 'rank' | 'game' | 'start' | 'end' | 'duration' | 'gap' | 'status' | 'actions'

// Inset of the grid's outer edges. Carried by the rank / actions cells rather
// than the table, so rows (and the expanded block's purple edge) still span
// the card edge to edge.
export const TABLE_GUTTER = 24
const RANK_CONTENT_WIDTH = 64
const ACTIONS_CONTENT_WIDTH = 120

export const DRILLDOWN_COLUMNS: { key: DrilldownColumnKey; width?: number }[] = [
  { key: 'rank', width: TABLE_GUTTER + RANK_CONTENT_WIDTH },
  { key: 'game' },
  { key: 'start', width: 116 },
  { key: 'end', width: 116 },
  { key: 'duration', width: 116 },
  { key: 'gap', width: 80 },
  { key: 'status', width: 110 },
  { key: 'actions', width: ACTIONS_CONTENT_WIDTH + TABLE_GUTTER },
]

export const DRILLDOWN_COLUMN_COUNT = DRILLDOWN_COLUMNS.length

// The game column has no fixed width: it takes whatever the fixed columns leave.
// On a narrow card that is ~0 and the neighbouring headers/cells overlap, so the
// table never shrinks below the fixed columns plus this much room for the game
// (the card then scrolls sideways instead).
export const GAME_COLUMN_MIN_WIDTH = 240
export const DRILLDOWN_TABLE_MIN_WIDTH = DRILLDOWN_COLUMNS.reduce((sum, column) => sum + (column.width ?? 0), 0) + GAME_COLUMN_MIN_WIDTH

export const GAME_ROW_HEIGHT = 60
export const GROUP_ROW_HEIGHT = 36
// Active / discontinued band separating the adopters, as in the tracked-game picker.
export const SECTION_ROW_HEIGHT = 36
export const EVENT_ROW_HEIGHT = 40
export const GAME_ICON_SIZE = 36
export const GAME_ICON_RADIUS = 10
export const GAME_ICON_GAP = 1.5
// Event names line up with the game name (icon + gap); row numbers sit under the rank.
export const EVENT_NAME_INDENT = GAME_ICON_SIZE + GAME_ICON_GAP * 8
export const EVENT_NUMBER_INDENT = 24
export const DURATION_BAR_MAX_WIDTH = 40

/**
 * Class hooks for the cells whose horizontal padding differs from the grid
 * default. They have to be styled from the table (see `DRILLDOWN_TABLE_SX`)
 * rather than per cell: `& .MuiTableCell-root` scores two classes, so a cell's
 * own one-class `sx` would always lose to it.
 */
export const DRILLDOWN_CELL_CLASS = {
  rank: 'DrilldownTable__rank',
  gameName: 'DrilldownTable__gameName',
  eventNumber: 'DrilldownTable__eventNumber',
  eventName: 'DrilldownTable__eventName',
  actions: 'DrilldownTable__actions',
}

export const DRILLDOWN_TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  minWidth: DRILLDOWN_TABLE_MIN_WIDTH,
  '& .MuiTableCell-root': { px: 1, py: 0, lineHeight: 1.3 },
  '& .MuiTableCell-head': {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    // Safety net: a cramped header ellipsizes rather than painting over its neighbour.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    py: 1.5,
  },
  // SortLabel pads the label to keep centered headers balanced; these are left-aligned.
  '& .MuiTableSortLabel-root.MuiTableSortLabel-root': { pl: 0 },
  // Keep the per-column padding last: it ties with the rules above on
  // specificity, so declaration order is what makes it win.
  [`& .${DRILLDOWN_CELL_CLASS.rank}`]: { pl: `${TABLE_GUTTER}px` },
  [`& .${DRILLDOWN_CELL_CLASS.gameName}`]: { pl: 0 },
  [`& .${DRILLDOWN_CELL_CLASS.eventNumber}`]: { pl: `${TABLE_GUTTER + EVENT_NUMBER_INDENT}px` },
  [`& .${DRILLDOWN_CELL_CLASS.eventName}`]: { pl: `${EVENT_NAME_INDENT}px` },
  [`& .${DRILLDOWN_CELL_CLASS.actions}`]: { pr: `${TABLE_GUTTER}px` },
} as const

/**
 * Pins the column header row while the page scrolls through a long adopters list. The cells are
 * opaque so rows never show through, and sit below the sticky hierarchy bar (z-index 10) at `top`,
 * which is the bar's own offset plus its height. Tables with `border-collapse: collapse` drop a
 * sticky cell's borders, hence the `separate` model on the table.
 */
export const DRILLDOWN_HEADER_Z_INDEX = 3

export const drilldownStickyHeaderSx = (top: number, pinned = true) =>
  ({
    borderCollapse: 'separate',
    borderSpacing: 0,
    // `pinned` is off while the table container scrolls sideways: it then becomes the header's
    // nearest scroll container (overflow-x: auto computes overflow-y to auto), so a sticky `top`
    // would push the header down over the first rows instead of pinning it to the page.
    '& .MuiTableCell-head': {
      bgcolor: 'background.paper',
      ...(pinned ? { position: 'sticky', top, zIndex: DRILLDOWN_HEADER_Z_INDEX } : {}),
    },
  } as const)

export const gameDividerColor = (theme: Theme) => alpha(theme.palette.text.primary, 0.06)
export const eventDividerColor = (theme: Theme) => alpha(theme.palette.primary.main, 0.1)

/** Rows inside an expanded game block: faint purple tint + 3px brand edge on the first cell. */
export const expandedBlockRowSx = (theme: Theme) => ({
  bgcolor: alpha(theme.palette.primary.main, 0.025),
  '& > td:first-of-type': { boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}` },
})
